import React from "react";
import {
  strengthExerciseById,
  strengthExerciseCatalog,
} from "../utils/workoutCatalog";

const createExercise = () => ({
  id: `ex-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  exerciseId: "",
  sets: "",
  reps: "",
  weight: "",
  isOpen: true,
});

const createWorkout = () => ({
  id: `wk-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  sessionsPerWeek: "",
  durationMinutes: "",
  effort: "",
  exercises: [createExercise()],
  isOpen: true,
});

const isComplete = (exercise) =>
  !!exercise.exerciseId &&
  Number(exercise.sets) > 0 &&
  Number(exercise.reps) > 0 &&
  Number(exercise.weight) > 0;

const getExerciseLabel = (exerciseId) =>
  strengthExerciseById[exerciseId]?.label || "Select exercise";

export default function ExerciseList({
  workouts = [],
  setWorkouts,
  unit,
  onUnitChange,
}) {
  const allExercises = workouts.flatMap(
    (workout) => workout.exercises || []
  );
  const hasExercises = allExercises.some(
    (exercise) => !!exercise.exerciseId
  );
  const hasComplete = allExercises.some(isComplete);

  const addWorkout = () =>
    setWorkouts((prev) => [...prev, createWorkout()]);

  const removeWorkout = (id) =>
    setWorkouts((prev) =>
      prev.filter((workout) => workout.id !== id)
    );

  const updateWorkout = (id, updates) =>
    setWorkouts((prev) =>
      prev.map((workout) =>
        workout.id === id ? { ...workout, ...updates } : workout
      )
    );

  const toggleWorkoutOpen = (id) =>
    setWorkouts((prev) =>
      prev.map((workout) =>
        workout.id === id
          ? { ...workout, isOpen: !workout.isOpen }
          : workout
      )
    );

  const addExercise = (workoutId) =>
    setWorkouts((prev) =>
      prev.map((workout) =>
        workout.id === workoutId
          ? {
              ...workout,
              exercises: [
                ...(workout.exercises || []),
                createExercise(),
              ],
            }
          : workout
      )
    );

  const removeExercise = (workoutId, exerciseId) =>
    setWorkouts((prev) =>
      prev.map((workout) =>
        workout.id === workoutId
          ? {
              ...workout,
              exercises: (workout.exercises || []).filter(
                (exercise) => exercise.id !== exerciseId
              ),
            }
          : workout
      )
    );

  const updateExercise = (workoutId, exerciseId, updates) =>
    setWorkouts((prev) =>
      prev.map((workout) =>
        workout.id === workoutId
          ? {
              ...workout,
              exercises: (workout.exercises || []).map(
                (exercise) =>
                  exercise.id === exerciseId
                    ? { ...exercise, ...updates }
                    : exercise
              ),
            }
          : workout
      )
    );

  const toggleExerciseOpen = (workoutId, exerciseId) =>
    setWorkouts((prev) =>
      prev.map((workout) =>
        workout.id === workoutId
          ? {
              ...workout,
              exercises: (workout.exercises || []).map(
                (exercise) =>
                  exercise.id === exerciseId
                    ? { ...exercise, isOpen: !exercise.isOpen }
                    : exercise
              ),
            }
          : workout
      )
    );

  return (
    <div className="glass-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-display text-[var(--ink)]">
            Weekly strength workouts
          </h3>
          <p className="text-sm text-[var(--muted)]">
            Build one or more recurring workouts. Workout-based mode
            uses these to calculate calories.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--muted)]">
            Weight unit
          </span>
          <div className="segmented">
            {["kg", "lb"].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => onUnitChange(value)}
                className={`segmented-button ${
                  unit === value ? "is-active" : ""
                }`}
              >
                {value.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span
          className={`rounded-full px-3 py-1 ${
            hasExercises
              ? "bg-white/80 text-[var(--ink)]"
              : "bg-white/60 text-[var(--muted)]"
          }`}
        >
          {hasExercises
            ? "Strength detail boost active"
            : "Add 1+ exercise for +10% confidence"}
        </span>
        <span
          className={`rounded-full px-3 py-1 ${
            hasComplete
              ? "bg-white/80 text-[var(--ink)]"
              : "bg-white/60 text-[var(--muted)]"
          }`}
        >
          {hasComplete
            ? "Load detail boost active"
            : "Fill sets + reps + weight for +10%"}
        </span>
      </div>

      {workouts.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-white/70 bg-white/60 p-4 text-sm text-[var(--muted)]">
          No workouts added yet. Add your first workout to log sessions
          and exercises.
        </div>
      ) : null}

      <div className="mt-4 space-y-4">
        {workouts.map((workout, workoutIndex) => (
          <div
            key={workout.id}
            className="rounded-2xl border border-white/70 bg-white/70"
          >
            <div className="workout-row flex items-center justify-between gap-3 px-4 py-3">
              <button
                type="button"
                onClick={() => toggleWorkoutOpen(workout.id)}
                className="flex items-center gap-3 text-left"
              >
                <span className="text-sm font-semibold text-[var(--ink)]">
                  Workout {workoutIndex + 1}
                </span>
                <span className="text-xs text-[var(--muted)]">
                  {workout.sessionsPerWeek !== ""
                    ? `${workout.sessionsPerWeek} sessions/week`
                    : "Set schedule"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => removeWorkout(workout.id)}
                className="workout-row__remove text-xs text-[var(--accent)] hover:underline"
              >
                Remove
              </button>
            </div>

            <div
              className={`grid gap-4 overflow-hidden px-4 transition-all duration-500 ${
                workout.isOpen
                  ? "max-h-[5000px] pb-4 opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="rounded-2xl border border-white/70 bg-white/80 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
                  Workout schedule
                </p>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <label className="text-xs text-[var(--muted)]">
                    Sessions per week
                    <input
                      type="number"
                      min="0"
                      max="7"
                      className="input-field mt-1"
                      value={workout.sessionsPerWeek}
                      onChange={(event) =>
                        updateWorkout(workout.id, {
                          sessionsPerWeek: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label className="text-xs text-[var(--muted)]">
                    Minutes per workout (optional)
                    <input
                      type="number"
                      min="0"
                      className="input-field mt-1"
                      value={workout.durationMinutes}
                      onChange={(event) =>
                        updateWorkout(workout.id, {
                          durationMinutes: event.target.value,
                        })
                      }
                    />
                    <span className="mt-1 block text-[11px] text-[var(--muted)]">
                      Leave blank to estimate from sets and reps.
                    </span>
                  </label>
                  <label className="text-xs text-[var(--muted)]">
                    Workout effort
                    <select
                      className="select-field mt-1"
                      value={workout.effort}
                      onChange={(event) =>
                        updateWorkout(workout.id, {
                          effort: event.target.value,
                        })
                      }
                    >
                      <option value="">Select effort</option>
                      <option value="light">Light</option>
                      <option value="moderate">Moderate</option>
                      <option value="hard">Hard</option>
                    </select>
                    <span className="mt-1 block text-[11px] text-[var(--muted)]">
                      Defaults to Moderate if left blank.
                    </span>
                  </label>
                </div>
              </div>

              <div className="space-y-3">
                {(workout.exercises || []).map(
                  (exercise, exerciseIndex) => {
                    const exerciseMeta =
                      strengthExerciseById[exercise.exerciseId];
                    const isBodyweight =
                      !!exerciseMeta?.usesBodyweight;
                    return (
                      <div
                        key={exercise.id}
                        className="rounded-2xl border border-white/70 bg-white/80"
                      >
                      <div className="exercise-row flex items-center justify-between gap-3 px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            toggleExerciseOpen(
                              workout.id,
                              exercise.id
                            )
                          }
                          className="flex items-center gap-3 text-left"
                        >
                          <span className="text-sm font-semibold text-[var(--ink)]">
                            Exercise {exerciseIndex + 1}
                          </span>
                          <span className="text-xs text-[var(--muted)]">
                            {getExerciseLabel(exercise.exerciseId)}
                          </span>
                        </button>
                        <div className="exercise-actions flex items-center gap-3">
                          <span
                            className={`text-xs font-medium ${
                              isComplete(exercise)
                                ? "text-[var(--accent-2)]"
                                : "text-[var(--muted)]"
                            }`}
                          >
                            {isComplete(exercise) ? "Complete" : "Partial"}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              removeExercise(workout.id, exercise.id)
                            }
                            className="exercise-row__remove text-xs text-[var(--accent)] hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <div
                        className={`grid gap-3 overflow-hidden px-4 transition-all duration-500 ${
                          exercise.isOpen
                            ? "max-h-[560px] pb-4 opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="grid gap-3 md:grid-cols-3">
                          <label className="text-xs text-[var(--muted)] md:col-span-2">
                            Exercise selection
                            <select
                              className="select-field mt-1"
                              value={exercise.exerciseId}
                              onChange={(event) =>
                                updateExercise(
                                  workout.id,
                                  exercise.id,
                                  {
                                    exerciseId: event.target.value,
                                  }
                                )
                              }
                            >
                              <option value="">Select exercise</option>
                              {strengthExerciseCatalog.map((option) => (
                                <option
                                  key={option.id}
                                  value={option.id}
                                >
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="grid gap-3 md:grid-cols-3">
                          <label className="text-xs text-[var(--muted)]">
                            Sets (per session)
                            <input
                              type="number"
                              min="0"
                              className="input-field mt-1"
                              value={exercise.sets}
                              onChange={(event) =>
                                updateExercise(
                                  workout.id,
                                  exercise.id,
                                  {
                                    sets: event.target.value,
                                  }
                                )
                              }
                            />
                          </label>
                          <label className="text-xs text-[var(--muted)]">
                            Reps per set
                            <input
                              type="number"
                              min="0"
                              className="input-field mt-1"
                              value={exercise.reps}
                              onChange={(event) =>
                                updateExercise(
                                  workout.id,
                                  exercise.id,
                                  {
                                    reps: event.target.value,
                                  }
                                )
                              }
                            />
                          </label>
                          <label className="text-xs text-[var(--muted)]">
                            {isBodyweight
                              ? `Added weight (${unit})`
                              : `Weight (${unit})`}
                            <input
                              type="number"
                              min="0"
                              className="input-field mt-1"
                              value={exercise.weight}
                              onChange={(event) =>
                                updateExercise(
                                  workout.id,
                                  exercise.id,
                                  {
                                    weight: event.target.value,
                                  }
                                )
                              }
                            />
                            {isBodyweight ? (
                              <span className="mt-1 block text-[11px] text-[var(--muted)]">
                                Leave blank for bodyweight only.
                              </span>
                            ) : null}
                          </label>
                        </div>
                      </div>
                      </div>
                    );
                  }
                )}
              </div>

              <button
                type="button"
                onClick={() => addExercise(workout.id)}
                className="inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-4 py-2 text-sm font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                Add exercise to workout
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addWorkout}
        className="mt-4 inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-4 py-2 text-sm font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
      >
        Add workout
      </button>
    </div>
  );
}
